import * as React from 'react';

export interface TooltipTouchHandlers {
  onTouchStart: () => void;
  onTouchEnd: () => void;
  onTouchMove: () => void;
  onTouchCancel: () => void;
}

export interface UseTooltipTouchOptions {
  disabled?: boolean;
  longPressDuration?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
}

export interface UseTooltipTouchResult {
  open: boolean;
  setOpen: (open: boolean) => void;
  handlers: TooltipTouchHandlers;
}

export function useTooltipTouch({
  disabled = false,
  longPressDuration = 500,
  open: openProp,
  onOpenChange,
  defaultOpen,
}: UseTooltipTouchOptions = {}): UseTooltipTouchResult {
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const open = disabled ? false : isControlled ? openProp : internalOpen;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (disabled) return;
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [disabled, isControlled, onOpenChange],
  );

  const clearTimer = React.useCallback(() => clearTimeout(timerRef.current), []);

  React.useEffect(() => clearTimer, [clearTimer]);

  const handlers = React.useMemo<TooltipTouchHandlers>(
    () => ({
      onTouchStart: () => {
        clearTimer();
        timerRef.current = setTimeout(() => setOpen(true), longPressDuration);
      },
      onTouchEnd: clearTimer,
      onTouchMove: clearTimer,
      onTouchCancel: clearTimer,
    }),
    [setOpen, clearTimer, longPressDuration],
  );

  return {open, setOpen, handlers};
}
